<template>
  <section>
    <template v-if="isLoading || !isEmptyResults">
      <CashbackBottomSheet :name="CASHBACK_BOTTOMSHEET" />
      <!-- 256px = country slider, 32px = city selector -->
      <div
        class="no-scrollbar max-h-[calc(100vh-256px-32px-var(--status-bar-height))] space-y-3 overflow-y-auto pb-8"
      >
        <div v-if="isCitiesLoading || isCountriesLoading" class="space-y-3">
          <OffersCarouselShimmer v-for="i in 3" :key="i" />
        </div>
        <template v-else>
          <div
            v-for="(result, i) in allRoutesResults"
            :key="i"
            class="bg-background-neutral-secondary space-y-3 rounded-2xl p-4"
          >
            <OffersCarouselBlock
              :offers="result.routes"
              :is-loading="result.isLoading"
              :cashback-click-source="
                analyticsEventsLib.config
                  .SearchResultCardCashbackPriceClickSource.HOT_OFFERS
              "
              @onCashbackClick="bottomSheet.show(CASHBACK_BOTTOMSHEET)"
            >
              <h2
                v-if="result.city"
                class="text-text-neutral-primary text-base font-semibold"
              >
                {{
                  hotOffersFeatureLib.getMappedCityNameWithEmoji(result.city)
                }}
              </h2>
            </OffersCarouselBlock>
            <FreeButton
              v-if="!result.isLoading"
              type="button"
              class="gray-button w-full space-x-2"
              size="sm"
              @click="handleAllOffersClick(result.city.code)"
            >
              <CalendarIcon />
              <span> {{ $t('hotOffers.v2.allOffers') }} </span>
            </FreeButton>
          </div>
        </template>
      </div>
    </template>
    <EmptyResults
      v-else
      :title="$t('hotOffers.v2.emptyResults.title')"
      :description="$t('hotOffers.v2.emptyResults.description')"
    >
      <template #action>
        <button
          type="button"
          class="text-button-primary-default text-base font-semibold"
          @click="emit('onChangeCity')"
        >
          {{ $t('hotOffers.v2.emptyResults.action') }}
        </button>
      </template>
    </EmptyResults>
  </section>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import {
    CashbackBottomSheet,
    FreeButton,
    EmptyResults,
  } from 'freedom-shared';
  import { controllerModel } from 'shared';

  import {
    hotOffersModel,
    hotOffersLib as hotOffersFeatureLib,
  } from '@/features/hot-offers';
  import {
    OffersCarouselShimmer,
    OffersCarouselBlock,
  } from '@/features/offers';

  import { hotOffersApi } from '@/entities/hot-offers';

  import { analyticsEventsLib, getCityData } from '@/shared/lib';

  import CalendarIcon from '@/assets/images/calendar-icon.svg?component';

  const emit = defineEmits<{ onChangeCity: [] }>();

  const CASHBACK_BOTTOMSHEET = 'cashback-bottom-sheet';
  const bottomSheet = controllerModel.useBottomSheet();

  const hotOffersBottomSheet =
    hotOffersModel.useHotOffersRoutesBottomSheetController();

  const { origCityCode, hotCitiesCodes, isCitiesLoading, isCountriesLoading } =
    hotOffersModel.useHotOffers();

  const handleAllOffersClick = (destCityCode: string) => {
    analyticsEventsLib.hotOffersAllDatesClick({
      origCityCode: origCityCode.value,
      destCityCode,
    });
    hotOffersBottomSheet.show(destCityCode);
  };

  const allRoutesQueries = hotOffersApi.useAllRoutesQuery({
    origCityCode,
    destCitiesCodes: hotCitiesCodes,
  });

  const allRoutesResults = computed(() => {
    return allRoutesQueries.value
      .map(query => {
        return {
          routes: query.results || [],
          city:
            query.results &&
            query.results.length > 0 &&
            getCityData(query.results[0]),
          isLoading: query.isLoading,
        };
      })
      .filter(result => result.routes.length > 0);
  });

  const isLoading = computed(() => {
    return (
      allRoutesResults.value.some(result => result.isLoading) ||
      isCitiesLoading.value ||
      isCountriesLoading.value
    );
  });

  const isEmptyResults = computed(() => {
    return allRoutesResults.value.every(result => result.routes.length === 0);
  });
</script>
